import { useState } from 'react'
import { Modal } from '../ui/Modal'
import { usePersonas } from '../../hooks/usePersonas'

interface ContentTaskFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: ContentTaskFormData) => Promise<void>
  isLoading?: boolean
}

export interface ContentTaskFormData {
  persona_id: string
  content_type: string
  channel: string
  topic: string
  brief?: string
  keywords?: string[]
  word_count_target?: number
  include_research?: boolean
  research_query?: string
}

const contentTypes = [
  { value: 'blog_post', label: 'Blog Post' },
  { value: 'linkedin_post', label: 'LinkedIn Post' },
  { value: 'twitter_thread', label: 'Twitter Thread' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'facebook_post', label: 'Facebook Post' },
  { value: 'instagram_caption', label: 'Instagram Caption' },
]

const channels = [
  { value: 'blog', label: 'Blog' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'newsletter', label: 'Newsletter' },
]

export function ContentTaskForm({ isOpen, onClose, onSubmit, isLoading }: ContentTaskFormProps) {
  const { data: personas } = usePersonas()
  
  const [formData, setFormData] = useState<ContentTaskFormData>({
    persona_id: '',
    content_type: 'blog_post',
    channel: 'blog',
    topic: '',
    brief: '',
    keywords: [],
    word_count_target: 800,
    include_research: true,
    research_query: '',
  })

  const [keywordInput, setKeywordInput] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(formData)
    setFormData({
      persona_id: '',
      content_type: 'blog_post',
      channel: 'blog',
      topic: '',
      brief: '',
      keywords: [],
      word_count_target: 800,
      include_research: true,
      research_query: '',
    })
    setKeywordInput('')
    onClose()
  }

  const addKeyword = () => {
    if (keywordInput.trim()) {
      setFormData(prev => ({
        ...prev,
        keywords: [...(prev.keywords || []), keywordInput.trim()],
      }))
      setKeywordInput('')
    }
  }

  const removeKeyword = (index: number) => {
    setFormData(prev => ({
      ...prev,
      keywords: (prev.keywords || []).filter((_, i) => i !== index),
    }))
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Crear Tarea de Contenido" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Persona *</label>
          <select
            className="input"
            value={formData.persona_id}
            onChange={(e) => setFormData({ ...formData, persona_id: e.target.value })}
            required
          >
            <option value="">Seleccionar persona...</option>
            {personas?.map((persona) => (
              <option key={persona.id} value={persona.id}>
                {persona.name} ({persona.role})
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Tipo de Contenido *</label>
            <select
              className="input"
              value={formData.content_type}
              onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
              required
            >
              {contentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Canal *</label>
            <select
              className="input"
              value={formData.channel}
              onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
              required
            >
              {channels.map((channel) => (
                <option key={channel.value} value={channel.value}>
                  {channel.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="label">Tema *</label>
          <input
            type="text"
            className="input"
            placeholder="Ej: Transformación digital en retail"
            value={formData.topic}
            onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
            required
          />
        </div>

        <div>
          <label className="label">Brief</label>
          <textarea
            className="input"
            rows={4}
            placeholder="Descripción detallada de lo que debe incluir el contenido..."
            value={formData.brief}
            onChange={(e) => setFormData({ ...formData, brief: e.target.value })}
          />
        </div>

        <div>
          <label className="label">Keywords</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              className="input flex-1"
              placeholder="Agregar keyword..."
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addKeyword()
                }
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={addKeyword}
            >
              Agregar
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.keywords?.map((keyword, index) => (
              <span key={index} className="badge badge-info flex items-center gap-1">
                {keyword}
                <button
                  type="button"
                  onClick={() => removeKeyword(index)}
                  className="hover:text-red-600"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Palabras objetivo</label>
            <input
              type="number"
              className="input"
              value={formData.word_count_target || ''}
              onChange={(e) => setFormData({ 
                ...formData, 
                word_count_target: e.target.value ? parseInt(e.target.value) : undefined 
              })}
              min="100"
              max="5000"
            />
          </div>

          <div className="flex items-center pt-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.include_research}
                onChange={(e) => setFormData({ ...formData, include_research: e.target.checked })}
                className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
              />
              <span className="text-sm text-slate-700">Incluir investigación</span>
            </label>
          </div>
        </div>

        {formData.include_research && (
          <div>
            <label className="label">Query de investigación</label>
            <input
              type="text"
              className="input"
              placeholder="Ej: mercado fitness millennial CDMX 2026 — barreras de membresía anual"
              value={formData.research_query}
              onChange={(e) => setFormData({ ...formData, research_query: e.target.value })}
            />
            <p className="mt-1 text-xs text-slate-500">
              Escribe una query específica (tema + contexto + geografía) para obtener fuentes alineadas al contenido.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Creando...' : 'Crear Tarea'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
